// Shared bits for the car pages (Car Submissions, Car Catalog).
import { useEffect, useState } from 'react';
import { fetchAuthedImage, resolveImageUrl, type AdminCar } from '../api/client';

export function statusBadge(status: string): string {
  const styles: Record<string, string> = {
    approved: 'bg-green-100 text-green-700',
    ai_approved: 'bg-purple-100 text-purple-700',
    rejected: 'bg-red-100 text-red-700',
    duplicate: 'bg-blue-100 text-blue-700',
    pending: 'bg-amber-100 text-amber-700',
  };
  return styles[status] || styles.pending;
}

/** <img> for admin image URLs, which need the Basic auth header. */
export function AuthedImage({ src, alt, className }: { src: string | null; alt: string; className: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;
    setUrl(null);
    setFailed(false);
    if (!src) return;
    fetchAuthedImage(resolveImageUrl(src) ?? src)
      .then(u => { objectUrl = u; if (!cancelled) setUrl(u); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [src]);

  if (!src || failed || !url) {
    return <div className={`${className} bg-gray-200 flex items-center justify-center text-gray-400 text-xs`}>{src && !failed ? '…' : 'No image'}</div>;
  }
  return <img src={url} alt={alt} className={className} />;
}

export interface CarFormValues {
  name: string;
  description: string;
  year: string;
  series: string;
  color: string;
  toyNumber: string;
  barcode: string;
  tags: string;
}

const list = (v: unknown) => (Array.isArray(v) ? v.join(', ') : '');
const str = (v: unknown) => (v === null || v === undefined ? '' : String(v));

/** Form values from an existing item, or from the submitter's hints and the AI's suggestions for a new one. */
export function formFrom(
  car: AdminCar | null,
  ai?: Record<string, unknown> | null,
  hints?: { name?: string | null; barcode?: string | null; toyNumber?: string | null; year?: number | null; series?: string | null; color?: string | null },
): CarFormValues {
  const src = (car ?? ai ?? {}) as Record<string, unknown>;
  return {
    name: str(car?.name ?? hints?.name ?? ai?.name),
    description: str(src.description),
    year: str(car?.year ?? hints?.year ?? ai?.year),
    series: str(car?.series ?? hints?.series ?? ai?.series),
    color: str(car?.color ?? hints?.color ?? ai?.color),
    toyNumber: str(car?.toyNumber ?? hints?.toyNumber ?? ai?.toyNumber),
    barcode: str(car?.barcode ?? hints?.barcode),
    tags: list(src.tags),
  };
}

const split = (s: string) => s.split(',').map(x => x.trim()).filter(Boolean);
const orNull = (s: string) => (s.trim() === '' ? null : s.trim());

/** The PATCH body for /api/admin/cars/items/:id (or the `car` edits of a submission review). */
export function formToBody(v: CarFormValues): Record<string, unknown> {
  return {
    name: v.name.trim(),
    description: orNull(v.description),
    year: v.year.trim() === '' ? null : Number(v.year),
    series: orNull(v.series),
    color: orNull(v.color),
    toyNumber: orNull(v.toyNumber),
    barcode: orNull(v.barcode),
    tags: split(v.tags),
  };
}

const input = 'w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-gray-500 uppercase">{label}</span>
      {children}
    </label>
  );
}

export function CarForm({ value, onChange }: { value: CarFormValues; onChange: (v: CarFormValues) => void }) {
  const set = <K extends keyof CarFormValues>(k: K) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    onChange({ ...value, [k]: e.target.value });
  return (
    <div className="grid grid-cols-2 gap-3">
      <div className="col-span-2"><Field label="Name"><input className={input} value={value.name} onChange={set('name')} maxLength={80} /></Field></div>
      <div className="col-span-2"><Field label="Description"><textarea className={input} rows={2} value={value.description} onChange={set('description')} maxLength={500} /></Field></div>
      <Field label="Year"><input className={input} type="number" min="1950" value={value.year} onChange={set('year')} /></Field>
      <Field label="Series"><input className={input} value={value.series} onChange={set('series')} /></Field>
      <Field label="Color"><input className={input} value={value.color} onChange={set('color')} /></Field>
      <Field label="Toy number"><input className={input} value={value.toyNumber} onChange={set('toyNumber')} /></Field>
      <div className="col-span-2"><Field label="Barcode (UPC; many cars can share one)"><input className={input} value={value.barcode} onChange={set('barcode')} /></Field></div>
      <div className="col-span-2"><Field label="Tags (comma-separated)"><input className={input} value={value.tags} onChange={set('tags')} /></Field></div>
    </div>
  );
}

export function Modal({ title, onClose, children, footer }: { title: string; onClose: () => void; children: React.ReactNode; footer?: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[92vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold text-slate-800">{title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
        </div>
        <div className="p-4 space-y-4 overflow-y-auto [scrollbar-gutter:stable]">{children}</div>
        {footer && <div className="flex gap-2 p-4 border-t bg-gray-50 flex-wrap">{footer}</div>}
      </div>
    </div>
  );
}

export function FilterPills<T extends string>({ options, value, onChange }: { options: { value: T; label: string }[]; value: T; onChange: (v: T) => void }) {
  return (
    <div className="flex gap-1 flex-wrap">
      {options.map(o => (
        <button
          key={o.value}
          onClick={() => onChange(o.value)}
          className={`px-2.5 py-1 text-xs font-medium rounded transition-colors ${
            value === o.value ? 'bg-slate-700 text-white' : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export const button = {
  approve: 'px-3 py-2 text-sm font-medium text-white bg-green-500 rounded-lg hover:bg-green-600 disabled:opacity-50',
  ai: 'px-3 py-2 text-sm font-medium text-white bg-purple-500 rounded-lg hover:bg-purple-600 disabled:opacity-50',
  pending: 'px-3 py-2 text-sm font-medium text-gray-700 bg-amber-100 rounded-lg hover:bg-amber-200 disabled:opacity-50',
  reject: 'px-3 py-2 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 disabled:opacity-50',
  plain: 'px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50',
};
